// @ts-nocheck
import React from 'react';
import { getDynamicEntityPermissionKey } from '../const/moduleSubmodule';
import REGEX from '../const/Regex';

const ACCESS_REQUEST_TYPE = 'AccessRequest';

const isAccessRequestEntity = (entityType) =>
  entityType?.request_type === ACCESS_REQUEST_TYPE;

const filterOutAccessRequestEntities = (entityTypes = []) =>
  entityTypes.filter((et) => !isAccessRequestEntity(et));

const safeDecodePathSegment = (value = '') => {
  try {
    return decodeURIComponent(String(value));
  } catch {
    return String(value || '');
  }
};

const normalizeLabel = (value) =>
  String(value || '')
    .trim()
    .replace(REGEX.WHITESPACE_SEQUENCE, ' ');

const normalizeRouteSegment = (value = '') =>
  normalizeLabel(safeDecodePathSegment(value)).replace(
    REGEX.HYPHEN_GLOBAL,
    ' '
  );

const hasDynamicEntityAccess = (entityLabel, permissions = {}) => {
  if (permissions?.is_super_admin) {
    return true;
  }

  const rootPermissionKey = getDynamicEntityPermissionKey(entityLabel);
  const rootPermission = permissions?.[rootPermissionKey];
  const hasRootAccess = !['None'].includes(rootPermission);

  return hasRootAccess;
};

const getEntityRouteLabel = (entityType) => String(entityType?.label);

const getEntityRoutePath = (entityType) =>
  `/${encodeURIComponent(getEntityRouteLabel(entityType))}`;

const getAccessRequestEntity = (entityTypes = [], permissions = {}) =>
  entityTypes.find(
    (entityType) =>
      isAccessRequestEntity(entityType) &&
      hasDynamicEntityAccess(getEntityRouteLabel(entityType), permissions)
  ) || null;

const buildNormalizedEntityPath = (entityLabel, suffix = '') => {
  const normalizedLabel = normalizeLabel(entityLabel);
  return `/${encodeURIComponent(normalizedLabel)}${suffix}`;
};

const buildRoutePatternPath = (entityLabel, suffix = '') => {
  const normalizedLabel = normalizeLabel(entityLabel);
  return `/${normalizedLabel}${suffix}`;
};

const getCreateEntityRoutePath = (entityLabel, addSegmentLabel = entityLabel) =>
  buildNormalizedEntityPath(
    entityLabel,
    `/add-${normalizeLabel(addSegmentLabel).replace(REGEX.WHITESPACE_SEQUENCE, '-')}`
  );

const getEntityDetailsRoutePath = (
  entityLabel = 'Model',
  uniqueId = ':uniqueId'
) => buildNormalizedEntityPath(entityLabel, `/${uniqueId}`);

const getArchivedEntityRoutePath = (entityLabel) => {
  const normalizedLabel = normalizeLabel(entityLabel).replace(
    REGEX.WHITESPACE_SEQUENCE,
    '-'
  );
  return buildNormalizedEntityPath(entityLabel, `/archived-${normalizedLabel}`);
};

const getUniqueEntityTypes = (entityTypes = []) => {
  if (!Array.isArray(entityTypes) || entityTypes.length === 0) {
    return [];
  }

  const seen = new Set();

  return entityTypes.filter((entityType) => {
    const labelKey = normalizeLabel(getEntityRouteLabel(entityType));
    if (!labelKey || seen.has(labelKey)) {
      return false;
    }
    seen.add(labelKey);
    return true;
  });
};

const mapEntityTypesToRoutes = (entityTypes, createRouteObject) => {
  if (typeof createRouteObject !== 'function') {
    return [];
  }

  return getUniqueEntityTypes(entityTypes).map(createRouteObject);
};

const findMatchingEntityType = (entityTypes = [], ...segments) => {
  const normalizedSegments = segments
    .filter(Boolean)
    .map((segment) => normalizeRouteSegment(segment))
    .filter(Boolean);

  if (normalizedSegments.length === 0) {
    return null;
  }

  return (
    entityTypes.find((entityType) => {
      const entityLabel = normalizeRouteSegment(
        entityType?.label
      ).toLowerCase();
      return normalizedSegments.some(
        (segment) => segment.toLowerCase() === entityLabel
      );
    }) || null
  );
};

const buildDynamicCreateEntityRouteObjects = (
  entityTypes = [],
  CreateModelStepper,
  permissions = {}
) => {
  if (!Array.isArray(entityTypes) || !CreateModelStepper) {
    return [];
  }

  return mapEntityTypesToRoutes(entityTypes, (entityType) => {
    const entityLabel = getEntityRouteLabel(entityType);
    if (!hasDynamicEntityAccess(entityLabel, permissions)) {
      return null;
    }
    const buildCreateRoute = (addSegmentLabel, keySuffix) => ({
      name: `Add ${addSegmentLabel}`,
      key: `createEntity_${normalizeLabel(entityLabel)}${keySuffix}`,
      path: buildRoutePatternPath(
        entityLabel,
        `/add-${normalizeLabel(addSegmentLabel).replace(REGEX.WHITESPACE_SEQUENCE, '-')}`
      ).replace(REGEX.LEADING_SLASH, ''),
      element: React.createElement(CreateModelStepper, {
        key: `${entityType?.value || entityType?.label}-${keySuffix || 'default'}`,
      }),
      permission: `Inventory_${normalizeLabel(entityLabel)}`,
    });
    return [
      buildCreateRoute(entityLabel, ''),
      buildCreateRoute('Entity', '_entity'),
    ];
  })
    .filter(Boolean)
    .flat();
};

const buildDynamicEntityDetailRouteObjects = (
  entityTypes = [],
  EntityFactory,
  permissions = {}
) => {
  if (!Array.isArray(entityTypes) || !EntityFactory) {
    return [];
  }

  return mapEntityTypesToRoutes(entityTypes, (entityType) => {
    const entityLabel = getEntityRouteLabel(entityType);
    if (!hasDynamicEntityAccess(entityLabel, permissions)) {
      return null;
    }
    return {
      name: `Edit ${entityLabel}`,
      key: `editEntity_${normalizeLabel(entityLabel)}`,
      path: buildRoutePatternPath(entityLabel, `/:uniqueId`).replace(
        REGEX.LEADING_SLASH,
        ''
      ),
      element: React.createElement(EntityFactory, {
        key: entityType?.value || entityType?.label,
      }),
      permission: `Inventory_${normalizeLabel(entityLabel)}`,
    };
  }).filter(Boolean);
};

const buildDynamicArchivedEntityRouteObjects = (
  entityTypes = [],
  ArchivedEntity,
  permissions = {}
) => {
  if (!Array.isArray(entityTypes) || !ArchivedEntity) {
    return [];
  }

  return mapEntityTypesToRoutes(entityTypes, (entityType) => {
    const entityLabel = getEntityRouteLabel(entityType);
    if (!hasDynamicEntityAccess(entityLabel, permissions)) {
      return null;
    }
    return {
      name: `Archived ${entityLabel}`,
      key: `archivedEntity_${normalizeLabel(entityLabel)}`,
      path: buildRoutePatternPath(
        entityLabel,
        `/archived-${normalizeLabel(entityLabel).replace(REGEX.WHITESPACE_SEQUENCE, '-')}`
      ).replace(REGEX.LEADING_SLASH, ''),
      element: React.createElement(ArchivedEntity, {
        key: entityType?.value || entityType?.label,
        entityLabel,
        entityValue: entityType?.value,
      }),
      permission: `Inventory_${normalizeLabel(entityLabel)}`,
    };
  }).filter(Boolean);
};

const buildDynamicInventoryMenuItems = (entityTypes = [], permissions = {}) => {
  if (!Array.isArray(entityTypes) || entityTypes.length === 0) {
    return [];
  }

  return getUniqueEntityTypes(filterOutAccessRequestEntities(entityTypes))
    .map((entityType) => {
      const entityLabel = getEntityRouteLabel(entityType);
      if (!hasDynamicEntityAccess(entityLabel, permissions)) {
        return null;
      }
      return {
        name: entityType?.label,
        key: `entityType_${normalizeLabel(entityLabel)}`,
        route: getEntityRoutePath(entityType),
        permissionKey: `Inventory_${normalizeLabel(entityLabel)}`,
      };
    })
    .filter(Boolean);
};

const buildDynamicInventoryRouteObjects = (
  entityTypes = [],
  EntityList,
  permissions = {}
) => {
  if (!Array.isArray(entityTypes) || !EntityList) {
    return [];
  }

  return getUniqueEntityTypes(filterOutAccessRequestEntities(entityTypes))
    .map((entityType) => {
      const entityLabel = getEntityRouteLabel(entityType);
      if (!hasDynamicEntityAccess(entityLabel, permissions)) {
        return null;
      }
      return {
        name: entityType?.label,
        key: `entityRoute_${normalizeLabel(entityLabel)}`,
        path: buildRoutePatternPath(entityLabel).replace(
          REGEX.LEADING_SLASH,
          ''
        ),
        element: React.createElement(EntityList, {
          key: entityType?.value || entityType?.label,
          entity_label: entityType?.label,
          entity_value: entityType?.value,
        }),
        permission: `Inventory_${normalizeLabel(entityLabel)}`,
      };
    })
    .filter(Boolean);
};

const buildDynamicFallbackRouteObjects = (DynamicEntityRoute) => {
  if (!DynamicEntityRoute) {
    return [];
  }

  return [
    {
      path: ':entitySlug',
      element: React.createElement(DynamicEntityRoute, {
        variant: 'list',
      }),
    },
    {
      path: ':entitySlug/add-:entityAction',
      element: React.createElement(DynamicEntityRoute, {
        variant: 'create',
      }),
    },
    {
      path: ':entitySlug/archived-:entityAction',
      element: React.createElement(DynamicEntityRoute, {
        variant: 'archive',
      }),
    },
    {
      path: ':entitySlug/:uniqueId',
      element: React.createElement(DynamicEntityRoute, {
        variant: 'details',
      }),
    },
  ];
};

export {
  buildDynamicInventoryMenuItems,
  buildDynamicInventoryRouteObjects,
  buildDynamicFallbackRouteObjects,
  buildDynamicCreateEntityRouteObjects,
  buildDynamicEntityDetailRouteObjects,
  buildDynamicArchivedEntityRouteObjects,
  findMatchingEntityType,
  getArchivedEntityRoutePath,
  getCreateEntityRoutePath,
  getEntityDetailsRoutePath,
  getEntityRouteLabel,
  getEntityRoutePath,
  getUniqueEntityTypes,
  mapEntityTypesToRoutes,
  normalizeLabel,
  normalizeRouteSegment,
  safeDecodePathSegment,
  hasDynamicEntityAccess,
  getAccessRequestEntity,
  filterOutAccessRequestEntities,
  ACCESS_REQUEST_TYPE,
};
